import { contentTemplatesURL } from '@app/constants/ApiEndPoints';
import axios from 'axios';

// Template item structure matching DB schema
export interface ContentTemplate {
  id?: number;
  consent_template_type_id?: number; // From API response
  type?: string | number; // For form handling (can be ID or slug)
  title: string;
  content: string;
  version: string;
  is_active: boolean;
  template_owner_type?: string; // 'default' or 'franchise'
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export const getAllTemplates = async (page?: number, perPage?: number, type?: string, is_active?: boolean, title?: string) => {
  try {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (perPage) params.append('limit', perPage.toString());
    if (type) params.append('templateType', type);
    if (is_active !== undefined) params.append('isActive', is_active.toString());
    if (title) params.append('title', title);
    
    const response = await axios.get(`${contentTemplatesURL}?${params.toString()}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const createTemplate = async (payload: any) => {
  try {
    // Transform payload to match API expectations
    const apiPayload = {
      consent_template_type_id: payload.type,
      title: payload.title,
      content: payload.content,
      is_active: payload.is_active,
      ...(payload.franchise_id && { franchise_id: payload.franchise_id })
    };
    
    const response = await axios.post(`${contentTemplatesURL}/create`, apiPayload);
    if (response.status === 201) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const updateTemplate = async (id: number, payload: any) => {
  try {
    // Transform payload to match API expectations
    const apiPayload = {
      consent_template_type_id: payload.type,
      title: payload.title,
      content: payload.content,
      ...(payload.franchise_id && { franchise_id: payload.franchise_id })
    };
    
    const response = await axios.put(`${contentTemplatesURL}/${id}`, apiPayload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const deleteTemplate = async (id: number) => {
  try {
    const response = await axios.delete(`${contentTemplatesURL}/${id}`);
    if (response.status === 200) {
      return response.data;
    }
    return response?.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const toggleTemplateStatus = async (id: number, is_active: boolean) => {
  try {
    const response = await axios.patch(`${contentTemplatesURL}/status`, { id, is_active });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const duplicateTemplate = async (id: number, newTitle?: string) => {
  try {
    const response = await axios.post(`${contentTemplatesURL}/${id}/duplicate`, { newTitle });
    if (response.status === 201) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const getTemplateTypes = async () => {
  try {
    const response = await axios.get(`${contentTemplatesURL}/types`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const getTemplatesByFranchise = async (franchiseId: number, page?: number, perPage?: number, type?: string, is_active?: boolean, title?: string) => {
  try {
    const params = new URLSearchParams();
    params.append('franchiseId', franchiseId.toString());
    if (page) params.append('page', page.toString());
    if (perPage) params.append('limit', perPage.toString());
    if (type) params.append('templateType', type);
    if (is_active !== undefined) params.append('isActive', is_active.toString());
    if (title) params.append('title', title);
    
    const response = await axios.get(`${contentTemplatesURL}?${params.toString()}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const getTemplateHistory = async (franchiseId?: number, consentTemplateTypeId?: number) => {
  try {
    const payload: any = {};
    if (franchiseId) payload.franchise_id = franchiseId;
    if (consentTemplateTypeId) payload.consent_template_type_id = consentTemplateTypeId;
    
    const response = await axios.post(`${contentTemplatesURL}/history`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const getActiveTemplateByType = async (franchiseId: number, typeSlug: string) => {
  try {
    const params = new URLSearchParams();
    params.append('franchiseId', franchiseId.toString());
    params.append('templateType', typeSlug);
    params.append('isActive', 'true');
    
    const response = await axios.get(`${contentTemplatesURL}/details?${params.toString()}`);
    if (response.status === 200 && response.data?.success && response.data?.collections) {
      // Check if collections is not empty
      if (Object.keys(response.data.collections).length === 0) {
        return null;
      }
      return response.data.collections;
    }
    return null;
  } catch (error: any) {
    console.error('Error fetching template:', error);
    return null;
  }
};
